/**
 * SubmissionReminderDialog — pop-up shown when the backend's scheduled
 * `send_submission_reminders` task has landed an unread Notification on
 * the current user (title === 'Submit your timesheet'). Styled to match
 * the Friday reminder + PriorWeekBlockDialog so every "submit your
 * timesheet" surface reads the same across the app.
 *
 * Props:
 *   open      — dialog visibility
 *   message   — the notification body text (backend-formatted with the
 *               week range so we don't reconstruct it here)
 *   onDismiss — user closed without acting; parent marks the
 *               notification read and stops showing the dialog for it
 *   onAction  — user clicked Go to Timesheet; parent marks read +
 *               navigates
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle } from 'lucide-react';

export default function SubmissionReminderDialog({ open, message, onDismiss, onAction }) {
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onDismiss?.(); }}>
      <DialogContent
        className="sm:max-w-md [&>button:last-child]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="submission-reminder-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Submit your timesheet
          </DialogTitle>
          <DialogDescription>
            {message || 'Your timesheet is still in draft. Submit it now to keep the approval chain moving.'}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-2">
          <Button
            className="bg-blue-600 hover:bg-blue-700 w-full"
            onClick={onAction}
            data-testid="submission-reminder-action-btn"
          >
            Go to My Timesheet
          </Button>
          <Button
            variant="ghost"
            className="w-full text-slate-500"
            onClick={onDismiss}
            data-testid="submission-reminder-dismiss-btn"
          >
            Not now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
